const DAY_MS = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** "4:30" + "PM", in the viewer's own time zone and locale conventions (E2-S4). */
export function clockParts(date: Date, locale?: string): { time: string; meridiem: string } {
  const parts = new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: '2-digit' }).formatToParts(date);
  const meridiem = parts.find((p) => p.type === 'dayPeriod')?.value ?? '';
  const time = parts
    .filter((p) => p.type !== 'dayPeriod')
    .map((p) => p.value)
    .join('')
    .trim();
  return { time, meridiem: meridiem.toUpperCase() };
}

/** "Today", "Tomorrow", "Yesterday", a weekday within a week, otherwise a date. */
export function relativeDay(date: Date, now = new Date(), locale?: string): string {
  const days = Math.round((startOfDay(date) - startOfDay(now)) / DAY_MS);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days < 7) return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(date);
  if (days < -1 && days > -7) return new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(date);
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) }).format(date);
}

/**
 * When a signed-in device was last used: "Active today, 4:30 PM", "Active
 * yesterday", "Active Tue", "Active 3 Oct". A time ahead of this device's
 * clock (the two clocks disagree) counts as now.
 */
export function lastActive(date: Date, now = new Date(), locale?: string): string {
  const at = date.getTime() > now.getTime() ? now : date;
  const days = Math.round((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days === 0) {
    const { time, meridiem } = clockParts(at, locale);
    return `Active today, ${meridiem ? `${time} ${meridiem}` : time}`;
  }
  if (days === 1) return 'Active yesterday';
  if (days < 7) return `Active ${new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(at)}`;
  return `Active ${shortDate(at, now, locale)}`;
}

/** "Google", "Google or Apple", "Google, Microsoft or Apple" ("and" with `joiner`), for a sentence. */
export function joinList(items: readonly string[], joiner: 'or' | 'and' = 'or'): string {
  return items.length <= 1 ? (items[0] ?? '') : `${items.slice(0, -1).join(', ')} ${joiner} ${items.at(-1)}`;
}

/**
 * The devices a sign-out would sign out, for a sentence: "Chrome on Windows
 * and Meet Dost app on iPhone", or "5 devices: A, B (2) and others". Devices
 * of one kind are named once, with how many there are.
 */
export function deviceList(devices: ReadonlyArray<{ device: string }>): string {
  const counts = new Map<string, number>();
  for (const d of devices) counts.set(d.device, (counts.get(d.device) ?? 0) + 1);
  const names = [...counts].map(([name, n]) => (n > 1 ? `${name} (${n})` : name));
  if (names.length <= 2) return names.join(' and ');
  return `${devices.length} devices: ${names[0]}, ${names[1]} and others`;
}

/** When a device signed in, to follow "Signed in": "today", "yesterday", "on 3 Oct", "on 3 Oct 2025". */
export function signedInOn(date: Date, now = new Date(), locale?: string): string {
  const days = Math.round((startOfDay(now) - startOfDay(date)) / DAY_MS);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  return `on ${shortDate(date, now, locale)}`;
}

/** "3 Oct", with the year when it is not this one. */
function shortDate(date: Date, now: Date, locale?: string): string {
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', ...(date.getFullYear() === now.getFullYear() ? {} : { year: 'numeric' }) }).format(date);
}

/** The day for the end of a sentence: "today", "yesterday", "on Sat", "on Apr 20" ("Added today", "Recorded on Apr 20"). */
export function onDay(date: Date, now = new Date(), locale?: string): string {
  const day = relativeDay(date, now, locale);
  return day === 'Today' || day === 'Yesterday' || day === 'Tomorrow' ? day.toLowerCase() : `on ${day}`;
}

/** "42 min", "1 h 5 min", "under a minute". */
export function formatDuration(seconds: number): string {
  if (seconds < 60) return 'under a minute';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h} h` : `${h} h ${m} min`;
}

/** Elapsed time as "12:48" or "1:02:03". */
export function formatElapsed(ms: number): string {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(s).padStart(2, '0')}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(0, Math.round(bytes / 1024))} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
}

export function people(count: number): string {
  return count === 1 ? '1 person' : `${count} people`;
}

/** Value for <input type="date"> / <input type="time"> in local time. */
export function localInputValues(date: Date): { date: string; time: string } {
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`,
    time: `${pad(date.getHours())}:${pad(date.getMinutes())}`,
  };
}

/** Combines local date and time inputs into a Date, or null if either is invalid. */
export function fromLocalInputs(date: string, time: string): Date | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t) return null;
  const result = new Date(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
  return Number.isNaN(result.getTime()) ? null : result;
}

/** What the recordings list says under a recording that has no file, or null when it has one or will. */
export function recordingProblem(rec: { status: string; failure: 'empty' | 'recorder' | 'processing' | null }): string | null {
  if (rec.status !== 'failed') return null;
  if (rec.failure === 'empty') return 'Nothing was recorded: no microphone, camera or shared screen sent anything while it ran.';
  if (rec.failure === 'recorder') return 'Recording failed: it stopped because of a problem on the server, before anything was saved.';
  return 'Processing failed. The original tracks are kept on the server.';
}

/** What the record dialog says: who will see it, and where the recording goes. */
export function recordingDestination(integration: { name: string | null } | null): string {
  const notice = 'Everyone will see a REC badge and a notice that the meeting is being recorded.';
  if (!integration) return `${notice} The recording appears under your Recordings when it has finished processing.`;
  if (integration.name) return `${notice} The recording is sent to ${integration.name} when it has finished processing, and is kept by the account that connected ${integration.name}.`;
  return `${notice} The recording is kept by the account that owns this meeting.`;
}

/** How long ago something happened, for a list that has to be answered in time: "just now", "12 min ago", "5 h ago", "3 days ago". */
export function timeAgo(date: Date, now = new Date()): string {
  const minutes = Math.max(0, Math.floor((now.getTime() - date.getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} days ago`;
}
